import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import './HouseHoldMemberScreen.scss';
import {
  getHouseholdMembersThunk,
  removeHouseholdMemberThunk,
} from '../../api/householdMemberSlice';
import { AppDispatch, RootState, useAppSelector } from '../../../../store';
import HouseHoldMemberEmptyIcon from '../../../../assets/images/icons/household-member-empty.png';
import getAge from '../../../../utils/getAge';
import { MINOR_AGE } from '../../../../config';
import memberToRemoveParams from '../../../../utils/memberToremoveParams';
import { Member } from '../../types';
import { user } from '../../../account/api/accountSlice';
import alert from '../../../../utils/alert';
import ConfirmationModal from '../../../app/components/ConfirmationModal';
import AddOrEditMemberModal from './AddHouseholdMember';
import { Spinner } from 'react-bootstrap';
import getMemberFullName from '../../../../utils/getMemberFullName';
import { useNavigate } from 'react-router-dom';

const HouseHoldMembers = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { household, isLoading } = useSelector((state: RootState) => state.householdMember);
  const accessToken = useSelector((state: RootState) => state.auth?.accessToken);
  const userInfo = useAppSelector(user);
  const isHouseholdMember = userInfo?.headOfHousehold != null;
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [isRemoveMode, setIsRemoveMode] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');

  useEffect(() => {
    if (isHouseholdMember) {
      navigate('/app/settings/account-details');
    }
  }, [dispatch, isHouseholdMember, navigate]);

  useEffect(() => {
    if (accessToken) {
      dispatch(getHouseholdMembersThunk({ accessToken }));
    }
  }, [dispatch, accessToken]);

  const hasMembers = household?.member && household.member.length > 0;

  const handleRemoveMember = useCallback(() => {
    setIsRemoveMode((prev) => !prev);
  }, []);

  const handleAddMemberClick = useCallback(() => {
    setModalMode('add');
    setShowAddModal(true);
  }, []);

  const handleCloseAddModal = useCallback(() => {
    setShowAddModal(false);
  }, []);

  const cancelRemoveMode = useCallback(() => {
    setShowModal(false);
    setIsRemoveMode(false);
    setSelectedMember(null);
  }, []);

  const confirmRemoveMember = useCallback(() => {
    if (selectedMember && userInfo?.identifier?.[0]?.value && accessToken) {
      const params = memberToRemoveParams(selectedMember, userInfo.identifier[0].value);
      dispatch(
        removeHouseholdMemberThunk({
          memberData: params,
          accessToken: accessToken,
        }),
      )
        .unwrap()
        .then(() => {
          dispatch(getHouseholdMembersThunk({ accessToken }));
          cancelRemoveMode();
          alert.success(t('You have removed member from your household list.'));
        })
        .catch(() => {
          alert.error(t('Something went wrong. Please try again.'));
        });
    }
  }, [selectedMember, userInfo, accessToken, dispatch, t, cancelRemoveMode]);

  const isMinor = useCallback((member: Member) => {
    return !!member?.birthDate && getAge(new Date(member.birthDate)) < MINOR_AGE;
  }, []);

  const handleMemberSelect = useCallback(
    (member: Member) => {
      if (isRemoveMode) {
        setSelectedMember(member);
        setShowModal(true);
      } else if (isMinor(member)) {
        setSelectedMember(member);
        setModalMode('edit');
        setShowAddModal(true);
      }
    },
    [isRemoveMode, isMinor],
  );

  if (isLoading) {
    return (
      <div className='spinner-container'>
        <Spinner className='spinner-icon' />
      </div>
    );
  }

  return (
    <div className='household-members'>
      <div className='content'>
        {hasMembers ? (
          <ul className='list-group'>
            {household.member.map((member) => {
              const fullName = getMemberFullName(member);
              const initials =
                `${fullName?.firstName?.[0] ?? ''}${fullName?.lastName?.[0] ?? ''}`.toUpperCase();
              const isMemberMinor = isMinor(member as Member);
              const isActive = member?.loginCount && member.loginCount > 0;
              return (
                // eslint-disable-next-line
                <li
                  key={member?.entity?.reference}
                  className='list-group-item d-flex'
                  onClick={() => handleMemberSelect(member as Member)}
                >
                  <div className='member-avatar me-3'>{initials}</div>
                  <div className='member-details flex-grow-1'>
                    <div className='member-name'>{`${fullName?.firstName}${fullName?.middleNameInitial} ${fullName?.lastName}`}</div>
                    <div className='member-role text-muted'>
                      {typeof member?.relationship?.text === 'string'
                        ? t(member.relationship.text)
                        : t('Member')}
                    </div>
                    {isMemberMinor && <div className='member-status minor'>{t('Minor')}</div>}
                  </div>
                  <div className={`member-status ${isActive ? 'active' : 'pending'} me-3`}>
                    <div className='member-status-circle' />
                    <div className='member-status-text'>
                      {isActive ? t('Active') : t('Pending')}
                    </div>
                  </div>
                  {isRemoveMode && (
                    <div className='checkbox'>
                      <input
                        type='checkbox'
                        className='checkbox-input'
                        checked={selectedMember?.entity?.reference === member?.entity?.reference}
                        onChange={() => {}}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        ) : (
          <div className='empty-household'>
            <div className='empty-household__content'>
              <img
                src={HouseHoldMemberEmptyIcon}
                alt='Empty household'
                className='empty-household__icon'
              />
              <h2 className='empty-household__title'>
                {t('Your household member list will appear here.')}
              </h2>
              <p className='empty-household__subtitle'>
                {t(
                  'To start adding members to your household list, tap on the “Add new member” button.',
                )}
              </p>
            </div>
          </div>
        )}
      </div>
      <div className='sticky-footer'>
        <div className='member-actions'>
          <button className='btn btn-primary' onClick={handleAddMemberClick}>
            <span className='button-text'>{t('Add new member')}</span>
          </button>
          {hasMembers && (
            <button className='btn btn-outline-danger' onClick={handleRemoveMember}>
              <span className='button-text'>{t('Remove member')}</span>
            </button>
          )}
        </div>
      </div>

      {selectedMember && (
        <ConfirmationModal
          showModal={showModal}
          onHide={cancelRemoveMode}
          onConfirm={confirmRemoveMember}
          title={t('Remove Household Member')}
          bodyText={t('Are you sure you want to remove name from your household?', {
            name: selectedMember
              ? `${getMemberFullName(selectedMember).firstName} ${getMemberFullName(selectedMember).middleNameInitial} ${getMemberFullName(selectedMember).lastName}`.trim()
              : '',
          })}
          confirmButtonText={t('Yes')}
          cancelButtonText={t('No')}
          iconAlt={t('Remove Member')}
        />
      )}
      <AddOrEditMemberModal
        show={showAddModal}
        handleClose={handleCloseAddModal}
        userInfo={userInfo}
        mode={modalMode}
        selectedMember={modalMode === 'edit' ? selectedMember : undefined}
      />
    </div>
  );
};

export default HouseHoldMembers;
